import { baseApi } from 'shared/api/baseApi'
import { CMS_EVENTS_TAG } from 'shared/api/tags'
import type {
    CMSCreateTranslationRequest,
    CMSCreateEventRequest,
    CMSEvent,
    CMSEventListParams,
    CMSGenerateUploadUrlResponse,
    CMSPaginatedResponse,
    CMSUpdateEventRequest,
} from '../model/types'

export const cmsEventsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getCmsEvents: build.query<
            CMSPaginatedResponse<CMSEvent>,
            CMSEventListParams
        >({
            query: ({ organizationId, ...params }) => ({
                url: `organizations/${organizationId}/cms-events`,
                method: 'GET',
                params,
            }),
            providesTags: [CMS_EVENTS_TAG],
        }),

        getCmsEvent: build.query<
            CMSEvent,
            { organizationId: string; eventId: string }
        >({
            query: ({ organizationId, eventId }) => ({
                url: `organizations/${organizationId}/cms-events/${eventId}`,
                method: 'GET',
            }),
            providesTags: [CMS_EVENTS_TAG],
        }),

        createCmsEvent: build.mutation<CMSEvent, CMSCreateEventRequest>({
            query: ({ organizationId, ...body }) => ({
                url: `organizations/${organizationId}/cms-events`,
                method: 'POST',
                body,
            }),
            invalidatesTags: [CMS_EVENTS_TAG],
        }),

        updateCmsEvent: build.mutation<CMSEvent, CMSUpdateEventRequest>({
            query: ({ organizationId, eventId, data }) => ({
                url: `organizations/${organizationId}/cms-events/${eventId}`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: [CMS_EVENTS_TAG],
        }),

        generateCmsEventUploadUrl: build.mutation<
            CMSGenerateUploadUrlResponse,
            { organizationId: string; eventId: string }
        >({
            query: ({ organizationId, eventId }) => ({
                url: `organizations/${organizationId}/cms-events/${eventId}/photos/generate-upload-url`,
                method: 'POST',
            }),
        }),

        // Upload before the event is saved; the key is not tied to an id.
        generateNewCmsEventUploadUrl: build.mutation<
            CMSGenerateUploadUrlResponse,
            { organizationId: string }
        >({
            query: ({ organizationId }) => ({
                url: `organizations/${organizationId}/cms-events/photos/generate-upload-url`,
                method: 'POST',
            }),
        }),

        getCmsEventPreviewId: build.mutation<
            { previewId: string },
            { organizationId: string; eventId: string }
        >({
            query: ({ organizationId, eventId }) => ({
                url: `organizations/${organizationId}/cms-events/${eventId}/preview`,
                method: 'POST',
            }),
        }),

        createCmsEventTranslation: build.mutation<
            CMSEvent,
            CMSCreateTranslationRequest
        >({
            query: ({ organizationId, id, locale }) => ({
                url: `organizations/${organizationId}/cms-events/${id}/translations`,
                method: 'POST',
                body: { locale },
            }),
            invalidatesTags: [CMS_EVENTS_TAG],
        }),

        publishCmsEvent: build.mutation<void, { orgId: string; id: string }>({
            query: ({ orgId, id }) => ({
                url: `organizations/${orgId}/cms-events/${id}/publish`,
                method: 'POST',
            }),
            invalidatesTags: [CMS_EVENTS_TAG],
        }),
    }),
})

export const {
    useGetCmsEventsQuery,
    useGetCmsEventQuery,
    useLazyGetCmsEventQuery,
    useCreateCmsEventMutation,
    useUpdateCmsEventMutation,
    useGenerateCmsEventUploadUrlMutation,
    useGenerateNewCmsEventUploadUrlMutation,
    useGetCmsEventPreviewIdMutation,
    usePublishCmsEventMutation,
    useCreateCmsEventTranslationMutation,
} = cmsEventsApi
